/**
 * Language picker — globe icon → dialog with locale list, progress chips,
 * "Help translate" links, and a "Request your language" form that writes
 * to the locale_requests table.
 *
 * Phase 1 of the i18n rollout: visible to all users so we can collect
 * demand signal even before non-English locales ship.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Globe, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LOCALES, type LocaleDefinition } from "@/i18n/config";
import { useLocalePreference } from "@/hooks/use-locale-preference";
import { supabase } from "@/integrations/supabase/client";

interface LanguagePickerProps {
  /** Render as a header icon button (default) or full-width Settings row. */
  variant?: "icon" | "row";
  className?: string;
}

export function LanguagePicker({ variant = "icon", className }: LanguagePickerProps) {
  const { t } = useTranslation();
  const { current, setLocale, isUpdating } = useLocalePreference();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [requestMode, setRequestMode] = useState(false);
  const [requestLang, setRequestLang] = useState("");
  const [requestNote, setRequestNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /**
   * Marketing pages use URL-based locale (path prefix) for SEO. When the
   * picker switches between supported marketing locales on those routes,
   * also navigate so the URL matches the chosen locale (the page reads
   * locale from the path, not i18next).
   */
  const MARKETING_PATHS = ["/", "/rsvp-info", "/rpe-info", "/feedback-info"];
  const PREFIX_BY_CODE: Record<string, string> = {
    es: "es",
    "pt-BR": "pt-br",
    fr: "fr",
  };
  const KNOWN_PREFIXES = Object.values(PREFIX_BY_CODE);
  const stripPrefix = (path: string): string => {
    const lower = path.toLowerCase();
    for (const p of KNOWN_PREFIXES) {
      if (lower === `/${p}`) return "/";
      if (lower.startsWith(`/${p}/`)) return path.slice(p.length + 1);
    }
    return path;
  };
  const getMarketingTarget = (toCode: string): string | null => {
    const path = location.pathname;
    const canonical = stripPrefix(path);
    if (!MARKETING_PATHS.includes(canonical)) return null;
    // English / unknown locale → unprefixed canonical
    const prefix = PREFIX_BY_CODE[toCode];
    if (!prefix) {
      // For any en* code or unsupported marketing locale, send to canonical
      if (toCode === "en" || toCode.startsWith("en")) {
        return canonical === path ? null : canonical;
      }
      // Other unsupported locales: still strip prefix so we don't strand users
      return canonical === path ? null : canonical;
    }
    const target = canonical === "/" ? `/${prefix}` : `/${prefix}${canonical}`;
    return target === path ? null : target;
  };


  const handlePick = async (locale: LocaleDefinition) => {
    if (locale.status === "coming_soon") {
      // Treat tap on a coming-soon locale as a soft request signal
      void supabase.from("locale_requests").insert({
        locale_code: locale.code,
        language_name: locale.englishName,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      toast.info(
        t("languagePicker.comingSoonToast", {
          english: locale.englishName,
          native: locale.nativeName,
        })
      );
      return;
    }
    const isBeta = locale.status === "in_progress";
    if (locale.code !== current?.code) {
      await setLocale(locale.code);
      const target = getMarketingTarget(locale.code);
      if (target) navigate(target);
      if (isBeta) {
        // Honest expectation-setting once they switch into a partial locale
        toast.info(
          t("languagePicker.betaToast", { language: locale.nativeName })
        );
      }
    } else {
      // Same locale picked, but URL might still be in the wrong shape (e.g.
      // user landed on /es directly with stored ES preference and switches
      // explicitly — no-op). Still align URL if needed.
      const target = getMarketingTarget(locale.code);
      if (target) navigate(target);
    }
    setOpen(false);
  };

  const handleRequestSubmit = async () => {
    const trimmed = requestLang.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase.from("locale_requests").insert({
        locale_code: trimmed.toLowerCase().slice(0, 16),
        language_name: trimmed,
        note: requestNote.trim() || null,
        user_id: session?.user?.id ?? null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      if (error) throw error;
      toast.success(t("languagePicker.requestSubmitted"));
      setRequestLang("");
      setRequestNote("");
      setRequestMode(false);
    } catch (err) {
      console.error("[LanguagePicker] request failed", err);
      toast.error(t("languagePicker.requestFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  // Short uppercase code for the badge — "en" → "EN", "pt-BR" → "PT"
  const shortCode = (current?.code ?? "en").split("-")[0].toUpperCase();
  const tooltipLabel = current?.englishName
    ? `Language: ${current.englishName}`
    : t("languagePicker.trigger");

  const triggerButton =
    variant === "icon" ? (
      <Button
        variant="ghost"
        size="sm"
        className={cn("h-9 px-2 gap-1.5", className)}
        aria-label={tooltipLabel}
      >
        <Globe className="h-4 w-4" />
        <span className="text-[11px] font-semibold tracking-wide leading-none">
          {shortCode}
        </span>
      </Button>
    ) : (
      <Button variant="outline" className={cn("w-full justify-between", className)}>
        <span className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          {t("languagePicker.trigger")}
        </span>
        <span className="text-xs text-muted-foreground">
          {current?.nativeName ?? current?.englishName ?? "English"}
        </span>
      </Button>
    );

  // Group locales by status so users see what's actually usable first.
  // "Available" = shipped + beta (in_progress); "Coming soon" = the rest.
  const availableLocales = LOCALES.filter(
    (l) => l.status === "shipped" || l.status === "in_progress"
  );
  const comingSoonLocales = LOCALES.filter((l) => l.status === "coming_soon");
  const sections: Array<{ key: string; label: string; items: LocaleDefinition[] }> = [
    { key: "available", label: "Available now", items: availableLocales },
    { key: "coming", label: "Coming soon", items: comingSoonLocales },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setRequestMode(false);
      }}
    >
      {variant === "icon" ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>{triggerButton}</DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>{tooltipLabel}</TooltipContent>
        </Tooltip>
      ) : (
        <DialogTrigger asChild>{triggerButton}</DialogTrigger>
      )}
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("languagePicker.title")}</DialogTitle>
          <DialogDescription>{t("languagePicker.subtitle")}</DialogDescription>
        </DialogHeader>

        {!requestMode ? (
          <>
            <ScrollArea className="max-h-[60vh] pe-2">
              <div className="space-y-4">
                {sections.map((section) => {
                  if (!section.items.length) return null;
                  return (
                    <div key={section.key} className="space-y-1">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground px-1">
                        {section.label}
                      </p>
                      {section.items.map((locale) => {
                        const isCurrent = current?.code === locale.code;
                        const isShipped = locale.status === "shipped" || locale.status === "in_progress";
                        return (
                          <button
                            key={locale.code}
                            type="button"
                            disabled={isUpdating}
                            onClick={() => handlePick(locale)}
                            className={cn(
                              "w-full flex items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-sm transition-colors",
                              "hover:bg-accent hover:text-accent-foreground",
                              isCurrent && "bg-accent/50",
                              !isShipped && "opacity-70"
                            )}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              {isCurrent ? (
                                <Check className="h-4 w-4 text-primary shrink-0" />
                              ) : (
                                <span className="h-4 w-4 shrink-0" aria-hidden />
                              )}
                              <div className="min-w-0">
                                <div className="font-medium truncate">{locale.nativeName}</div>
                                <div className="text-[11px] text-muted-foreground truncate">
                                  {locale.englishName}
                                </div>
                              </div>
                            </div>
                            <div className="shrink-0 flex items-center gap-1">
                              {locale.status === "shipped" && locale.completeness === 100 ? null : locale.status === "in_progress" ? (
                                <>
                                  <Badge variant="secondary" className="text-[10px]">
                                    {t("languagePicker.inProgress")}
                                  </Badge>
                                  {locale.completeness ? (
                                    <Badge variant="outline" className="text-[10px] text-muted-foreground">
                                      {t("languagePicker.completeness", { percent: locale.completeness })}
                                    </Badge>
                                  ) : null}
                                </>
                              ) : (
                                <Badge variant="outline" className="text-[10px] border-muted text-muted-foreground">
                                  {t("languagePicker.comingSoon")}
                                </Badge>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center text-xs text-muted-foreground"
              onClick={() => setRequestMode(true)}
            >
              {t("languagePicker.requestLanguage")} →
            </Button>
          </>
        ) : (
          <div className="space-y-3">
            <Input
              autoFocus
              placeholder={t("languagePicker.requestPlaceholder")}
              value={requestLang}
              onChange={(e) => setRequestLang(e.target.value)}
              maxLength={64}
            />
            <Textarea
              placeholder={t("languagePicker.requestNotePlaceholder")}
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
              maxLength={280}
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRequestMode(false)} disabled={submitting}>
                {t("languagePicker.cancel")}
              </Button>
              <Button onClick={handleRequestSubmit} disabled={submitting || !requestLang.trim()}>
                {t("languagePicker.submit")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
